import type { ShowLockedPeriods } from "@components/accounting/schedules/useScheduleLockedPeriodsDialog";
import { useCallback } from "react";
import { ScheduleDeleteJournalEntryMode } from "@common/types/domains/accounting/schedule";
import { useScheduleChange } from "@components/accounting/schedules/change/useScheduleChange";
import { SchedulePickerTemplateWithAnchors } from "@components/accounting/schedules/scheduleApplyConfirmHelpers";
import { InteractiveScheduleCellState } from "@components/invoices/views/scheduleCellState";
import { useRemoveInvoiceSchedule } from "./useRemoveInvoiceSchedule";

/**
 * Replace/remove wiring shared by the invoice and invoice-line Schedule cells:
 * the applied state is the current application, removal goes through the
 * lifecycle delete verb, and the cell only supplies its own apply.
 */
export function useInvoiceScheduleCellChange(options: {
    entityId: number | undefined;
    state: InteractiveScheduleCellState | null;
    applySchedule: (
        template: SchedulePickerTemplateWithAnchors,
    ) => Promise<boolean>;
    onScheduleChanged?: () => Promise<void> | void;
    showLockedPeriods?: ShowLockedPeriods;
}) {
    const { entityId, state, applySchedule, onScheduleChanged } = options;
    const { removeSchedule, isRemoving } = useRemoveInvoiceSchedule({
        onRemoved: onScheduleChanged,
        showLockedPeriods: options.showLockedPeriods,
    });
    const appliedState = state?.kind === "applied" ? state : null;

    const removeCurrent = useCallback(
        async (
            journalEntryMode: ScheduleDeleteJournalEntryMode,
        ): Promise<boolean> => {
            if (appliedState == null || entityId == null) {
                return false;
            }
            return await removeSchedule({
                entityId,
                scheduleApplicationId: appliedState.scheduleApplicationId,
                journalEntryMode,
            });
        },
        [appliedState, entityId, removeSchedule],
    );

    const change = useScheduleChange({
        source: "invoice",
        current: appliedState
            ? {
                  scheduleApplicationId: appliedState.scheduleApplicationId,
                  scheduleTemplateId: appliedState.scheduleTemplateId,
                  scheduleName: appliedState.scheduleName,
              }
            : null,
        applySchedule,
        removeCurrent,
    });

    return { ...change, isRemoving };
}
